import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

export interface MobileActionBarProps {
  onBack?: () => void;
  onAdvance?: () => void;
  canGoBack: boolean;
  advanceLabel?: string;
  advanceDisabled?: boolean;
  /** Default true. Pass false on the final step where the workspace owns its
   *  own terminal action (download / share). */
  showAdvance?: boolean;
}

/** Sticky bottom action bar — sm-only. Owns the primary CTA so the user can
 *  always reach Back / Continue without scrolling to find them.
 *
 *  Sized with `safe-area-inset-bottom` padding so it sits above the iOS home
 *  indicator. Hidden at md and up — the toolbar's Back/Next at the top is
 *  enough on larger screens. */
export const MobileActionBar: React.FC<MobileActionBarProps> = ({
  onBack,
  onAdvance,
  canGoBack,
  advanceLabel,
  advanceDisabled,
  showAdvance = true,
}) => {
  return (
    <div
      className="md:hidden fixed inset-x-0 bottom-0 z-30 flex items-center gap-3"
      style={{
        padding: '12px 16px calc(12px + env(safe-area-inset-bottom)) 16px',
        background: 'var(--hj-surface-1)',
        borderTop: '1px solid var(--hj-hairline)',
      }}
    >
      <button
        type="button"
        onClick={onBack}
        disabled={!canGoBack}
        className="inline-flex items-center justify-center gap-1 rounded-full text-[14px] font-medium transition-opacity disabled:cursor-not-allowed flex-shrink-0"
        style={{
          padding: '0 16px',
          minHeight: 44,
          background: 'transparent',
          border: '1px solid var(--hj-hairline)',
          color: 'var(--hj-ink-2)',
          opacity: canGoBack ? 1 : 0.4,
          cursor: canGoBack ? 'pointer' : 'not-allowed',
        }}
      >
        <ArrowLeft size={16} strokeWidth={2} />
        Back
      </button>
      {showAdvance && (
        <button
          type="button"
          onClick={onAdvance}
          disabled={advanceDisabled}
          className="inline-flex items-center justify-center gap-2 rounded-full text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed flex-1"
          style={{
            padding: '0 18px',
            minHeight: 48,
            background: 'var(--hj-blue)',
            border: '1px solid var(--hj-blue)',
            color: 'oklch(0.18 0.005 70)',
            opacity: advanceDisabled ? 0.5 : 1,
            cursor: advanceDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          {advanceLabel ?? 'Continue'}
          <ArrowRight size={16} strokeWidth={2} />
        </button>
      )}
    </div>
  );
};